import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

// Read-only activity view. Aggregates recent Rule fires, recent Workflow fires,
// scheduled/recurring job status, and a rough per-trigger breakdown of active
// conversations. All panels degrade gracefully when an endpoint is missing —
// an empty section is better than a page crash.

interface RuleActivityEntry {
  timestamp: string;
  ruleType: string;
  conversationId?: number | string;
  outcome?: string;
}

interface WorkflowActivityEntry {
  timestamp: string;
  workflowName: string;
  triggerType: string;
  conversationId?: number | string;
  outcome?: string;
}

interface ScheduledJobEntry {
  id: string;
  cron: string;
  nextExecution?: string;
  lastExecution?: string;
  state?: string;
}

interface ActiveBreakdown {
  inWorkflow: number;
  inRule: number;
  idleMatching: number;
  total: number;
}

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white mb-5">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        {typeof count === "number" && (
          <span className="text-xs text-slate-500">{count} entries</span>
        )}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export default function WhatsRunning() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<RuleActivityEntry[]>([]);
  const [workflows, setWorkflows] = useState<WorkflowActivityEntry[]>([]);
  const [jobs, setJobs] = useState<ScheduledJobEntry[]>([]);
  const [breakdown, setBreakdown] = useState<ActiveBreakdown | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function safeGet<T>(path: string, fallback: T): Promise<T> {
      try {
        const res = await api.messaging.get(path);
        if (!res.ok) return fallback;
        const data = await res.json();
        return (data ?? fallback) as T;
      } catch {
        return fallback;
      }
    }

    (async () => {
      const [r, w, j, b] = await Promise.all([
        safeGet<RuleActivityEntry[]>("Rules/activity", []),
        safeGet<WorkflowActivityEntry[]>("Workflows/activity", []),
        safeGet<ScheduledJobEntry[]>("Workflows/scheduled-jobs", []),
        safeGet<ActiveBreakdown | null>("Workflows/active-breakdown", null)
      ]);
      if (cancelled) return;
      setRules(Array.isArray(r) ? r.slice(0, 10) : []);
      setWorkflows(Array.isArray(w) ? w.slice(0, 10) : []);
      setJobs(Array.isArray(j) ? j : []);
      setBreakdown(b);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [api]);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-semibold text-slate-800">What's Running</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Live view of automations firing on your site — Rules, Workflows, and
          scheduled jobs. See <Link to="/rules" className="underline">Rules</Link>{" "}
          or <Link to="/workflows" className="underline">Workflows</Link> to edit.
        </p>
      </div>

      {breakdown && (
        <Section title="Active Conversations">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-semibold text-slate-800">{breakdown.total}</div>
              <div className="text-xs text-slate-500 mt-1">Total active</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-slate-800">{breakdown.inWorkflow}</div>
              <div className="text-xs text-slate-500 mt-1">In a workflow</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-slate-800">{breakdown.inRule}</div>
              <div className="text-xs text-slate-500 mt-1">In a rule</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-slate-800">{breakdown.idleMatching}</div>
              <div className="text-xs text-slate-500 mt-1">Idle-matching</div>
            </div>
          </div>
        </Section>
      )}

      <Section title="Rules firing recently" count={rules.length}>
        {rules.length === 0 ? (
          <p className="text-sm text-slate-500">No recent rule activity.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Time</th>
                <th className="py-2 font-medium">Rule type</th>
                <th className="py-2 font-medium">Conversation</th>
                <th className="py-2 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-600">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="py-2 text-slate-800">{r.ruleType}</td>
                  <td className="py-2 text-slate-600">{r.conversationId ?? "—"}</td>
                  <td className="py-2 text-slate-600">{r.outcome ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Workflows firing recently" count={workflows.length}>
        {workflows.length === 0 ? (
          <p className="text-sm text-slate-500">No recent workflow activity.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Time</th>
                <th className="py-2 font-medium">Workflow</th>
                <th className="py-2 font-medium">Trigger</th>
                <th className="py-2 font-medium">Conversation</th>
                <th className="py-2 font-medium">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {workflows.map((w, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-600">{new Date(w.timestamp).toLocaleString()}</td>
                  <td className="py-2 text-slate-800">{w.workflowName}</td>
                  <td className="py-2 text-slate-600">{w.triggerType}</td>
                  <td className="py-2 text-slate-600">{w.conversationId ?? "—"}</td>
                  <td className="py-2 text-slate-600">{w.outcome ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Scheduled / recurring jobs" count={jobs.length}>
        {jobs.length === 0 ? (
          <p className="text-sm text-slate-500">No scheduled jobs configured.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                <th className="py-2 font-medium">Job</th>
                <th className="py-2 font-medium">Cron</th>
                <th className="py-2 font-medium">Last run</th>
                <th className="py-2 font-medium">Next run</th>
                <th className="py-2 font-medium">State</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(j => (
                <tr key={j.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-2 text-slate-800">{j.id}</td>
                  <td className="py-2 text-slate-600 font-mono text-xs">{j.cron}</td>
                  <td className="py-2 text-slate-600">
                    {j.lastExecution ? new Date(j.lastExecution).toLocaleString() : "—"}
                  </td>
                  <td className="py-2 text-slate-600">
                    {j.nextExecution ? new Date(j.nextExecution).toLocaleString() : "—"}
                  </td>
                  <td className="py-2 text-slate-600">{j.state ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
